<template>
  <template v-if="removed">
    <WdsStack align-center gap-sm>
      <WdsButtonGhost
        :href="
          laravelNamedRoute(
            isWorker
              ? namedRoutes.frontend.contracts['my-contracts']
              : namedRoutes.frontend.profiles.index.engaged,
          )
        "
        secondary
      >
        <WdsStack align-center gap-sm>
          <WdsIcon name="arrow-left" :size="16" />
          {{ isWorker ? $t('Contracts') : $t('All hires') }}
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>
    <ContentLoadingErrorNotice
      :title="$t('This hire is no longer available')"
      :description="
        $t(
          'Looks like this hire has been cancelled or removed. If you think this is a mistake or need more info, please contact support.',
        )
      "
      hide-status-button
      :button-text="$t('Go back')"
      :on-button-click="() => router.back()"
    />
  </template>
  <HireInvitationPage v-else-if="invitationView" :hire-id="hireId" />
  <template v-else>
    <HirePageHeader
      :hire-id="hireId"
      @switch-tab="activeTab = $event"
      @hire-ended="handleReloading"
    />
    <WdsAlert
      v-if="sourceHireLocalId && viewingAsClient && hasUnactedClientChanges"
      warning
      icon
      class="m-b-10"
      data-test="unacted-client-changes-alert"
    >
      <WdsStack direction-column gap-xs>
        <strong>{{ $t('The client has requested changes to this hire') }}</strong>
        <span>{{ $t('Review the changes and amend your supplier hire to match.') }}</span>
        <WdsStack
          v-if="clientChangeDiff.length"
          direction-column
          gap-xs
          class="tw-mt-1"
          data-test="client-change-diff"
        >
          <span v-for="row in clientChangeDiff" :key="row.label">
            <strong>{{ row.label }}:</strong>
            {{ row.from }} → {{ row.to }}
          </span>
        </WdsStack>
        <a :href="`/hires/${sourceHireLocalId}/overview`" class="tw-underline">
          {{ $t('View client hire') }}
        </a>
      </WdsStack>
    </WdsAlert>
    <WdsAlert
      v-if="sourceHireLocalId && viewingAsClient && hasUnactedClientTermination"
      warning
      icon
      bordered
      class="m-b-10"
      data-test="unactioned-client-termination-alert"
    >
      <WdsStack direction-row gap-sm justify-space-between align-center>
        <WdsStack direction-column>
          <span class="tw-font-bold">{{ $t('The client ended this hire early') }}</span>
          <span
            v-if="hasClientTerminationDate && hasSupplierEndDate"
            data-test="client-termination-date"
          >
            {{
              $t("The client's hire now ends :clientDate. Yours still ends :supplierDate.", {
                clientDate: clientTerminationDate,
                supplierDate: supplierEndDate,
              })
            }}
          </span>
          <span v-else-if="hasClientTerminationDate" data-test="client-termination-date">
            {{
              $t("The client's hire now ends :clientDate.", { clientDate: clientTerminationDate })
            }}
          </span>
        </WdsStack>
        <WdsButton
          v-if="canEndSupplierHire"
          primary
          data-test="review-client-termination"
          @click="openEndSupplierHire"
        >
          {{ endSupplierHireLabel }}
        </WdsButton>
      </WdsStack>
    </WdsAlert>
    <SourceLinkBanner
      v-if="sourceHireLocalId && viewingAsClient"
      :message="sourceHireBannerMessage"
      :subtitle="
        sourceHire?.company?.name ? `${$t('Client')}: ${sourceHire.company.name}` : undefined
      "
      :avatar-url="sourceHire?.company?.avatar"
      :details="sourceHireDetails"
      :link-url="`/hires/${sourceHireLocalId}/overview`"
      :link-text="$t('View client hire')"
    />
    <SourceLinkBanner
      v-if="supplierHireId && viewerIsSupplier"
      :message="$t('This hire has a linked supplier hire')"
      :subtitle="supplierBannerSubtitle"
      :avatar-url="hireCompany?.avatar"
      :details="supplierBannerDetails"
      :link-url="`/hires/${supplierHireId}/overview`"
      :link-text="$t('View supplier hire')"
      variant="info"
    />
    <div class="hire-page-tabs" :class="{ 'hire-page-tabs--no-bar': hideTabBar }">
      <WdsTabs v-model:active-tab-key="activeTab">
        <WdsTab :title="$t('Overview')" tab-key="overview">
          <div class="tw-mt-4">
            <HirePageOverview :hire-id="hireId" />
          </div>
        </WdsTab>
        <WdsTab v-if="shouldShowChecklistsTab" :title="$t('Checklists')" tab-key="checklists">
          <template #title>
            <TabTitleBadge
              :title="$t('Checklists')"
              :count="`${checklistCounts.done}/${checklistCounts.total}`"
            />
          </template>
          <div class="tw-mt-4 tw-max-w-4xl">
            <HireChecklists :hire-id="hireId" />
          </div>
        </WdsTab>
        <WdsTab v-if="shouldShowPaymentsTab" :title="$t('Payments')" tab-key="payments">
          <template #title>
            <TabTitleBadge
              :title="$t('Payments')"
              :count="showPendingPaymentsIndicator ? pendingPaymentsCount : undefined"
              error
            />
          </template>
          <div class="tw-mt-4">
            <HirePagePayments :hire-id="hireId" />
          </div>
        </WdsTab>
        <WdsTab
          v-if="shouldShowClassificationTab"
          :title="$t('Classification')"
          tab-key="classification"
        >
          <template #title>
            <WdsStack align-center>
              {{ $t('Classification') }}
              <div
                v-show="classificationRequiresAction"
                class="tw-bg-red-600 tw-rounded-full tw-size-1 tw-ml-2"
              ></div>
            </WdsStack>
          </template>
          <div class="tw-mt-4">
            <HirePageClassification :hire-id="hireId" />
          </div>
        </WdsTab>
        <WdsTab
          v-if="approvalApprovablesCount > 0 && viewingAsClient"
          :title="$t('Approvals')"
          tab-key="approvals"
        >
          <template #title>
            <WdsStack align-center>
              {{ $t('Approvals') }}
              <div
                v-show="approvalsRequireActionCount"
                class="tw-bg-red-600 tw-rounded-full tw-size-1 tw-ml-2"
              ></div>
            </WdsStack>
          </template>
          <div class="tw-mt-4">
            <ApprovalOverviewPage :hire-id="hireId" @reloading-approvals="handleReloading" />
          </div>
        </WdsTab>
        <WdsTab v-if="shouldShowContractsTab" :title="$t('Contracts')" tab-key="contracts">
          <div class="tw-mt-4">
            <HirePageContracts :hire-id="hireId" />
          </div>
        </WdsTab>
      </WdsTabs>
    </div>
  </template>
</template>

<script lang="ts" setup>
import {
  WdsAlert,
  WdsTabs,
  WdsTab,
  WdsIcon,
  WdsButtonGhost,
  WdsButton,
  WdsStack,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import HirePageHeader from '@/components/Hire/HirePageHeader.vue'
import HireInvitationPage from '@/components/Hire/HireInvitationPage.vue'
import SourceLinkBanner from '@/components/SourceLinkBanner.vue'
import ApprovalOverviewPage from '@/components/Workflows/Approvals/Triggers/Hire/ApprovalOverviewPage.vue'
import HirePageOverview from '@/components/Hire/HirePageOverview.vue'
import HirePageClassification from '@/components/Hire/HirePageClassification.vue'
import TabTitleBadge from '@/components/Common/TabTitleBadge.vue'
import HireChecklists from '@/components/Checklists/HireChecklists.vue'
import { useHireChecklists } from '@/components/Checklists/useHireChecklists'
import HirePagePayments from '@/components/Hire/HirePagePayments.vue'
import HirePageContracts from '@/components/Hire/HirePageContracts.vue'
import { canViewContractHistory } from '@/components/Hire/contractHistory'
import { isHirePageTab } from '@/components/Hire/hirePageTabs'
import type { HirePageTab } from '@/components/Hire/hirePageTabs'
import useDate from '@/composables/useDate'
import { graphql } from '@/gql'
import { HireActiveStatus, PaymentRequestStatus } from '@/gql/graphql'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { useContractDiff } from '@/composables/useContractDiff'

import { useCancelHireModal } from '@/components/Hire/composables/useCancelHireModal'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'
import { useTerminateContractModal } from '@/components/Hire/composables/useTerminateContractModal'

interface Props {
  hireId: string
  removed: boolean
  tab?: string
  paymentsTabEnabled?: boolean
  invitationView?: boolean
}

const props = defineProps<Props>()

const route = useRoute()
const router = useRouter()

const currentTab = computed(() => {
  const { path } = route
  const match = path.match(/\/hires\/[^/]+\/([^/?]+)/)
  return match?.[1] ?? props.tab ?? 'overview'
})

const { user, isCompany, isWorker } = useAccountCapabilities()
const { openCancelHireModal } = useCancelHireModal()
const { openTerminateContractModal } = useTerminateContractModal()
const {
  viewerIsSupplier,
  recruiterManagesWorkers,
  loading: recruiterLoading,
} = useHireRecruiter(props.hireId)
const viewingAsClient = computed(() => isCompany.value && !viewerIsSupplier.value)

const { result, loading, refetch } = useQuery(
  graphql(`
    query HirePageData($approvable: ID!, $first: Int, $hire: ID!) {
      approvals: approvalApprovables(approvable: $approvable, first: $first) {
        paginatorInfo {
          count
        }
      }
      approvalsRequireAction: approvalApprovables(
        approvable: $approvable
        first: $first
        requiresAction: true
      ) {
        paginatorInfo {
          count
        }
      }
      hire(id: $hire) {
        id
        activeStatus
        usesClassification
        hasUnactedClientChanges
        hasUnactedClientTermination
        canTerminateContract
        canCancelContract
        activeContract {
          id
          endDate
        }
        classification {
          id
          isReclassificationRequired
          isClassificationActionRequired
        }
        sourceHire {
          id
          activeStatus
          terminationDate
          rate
          rateType
          currency
          startDate
          endDate
          job {
            name
          }
          company {
            name
            avatar
          }
          activeContract {
            id
            endDate
            startDate
            rate
            rateType
            currency
          }
          draftContract {
            id
            endDate
            startDate
            rate
            rateType
            currency
          }
        }
        supplierHire {
          id
        }
        company {
          name
          avatar
        }
        contracts(first: 1) {
          paginatorInfo {
            total
          }
        }
      }
    }
  `),
  {
    approvable: props.hireId,
    hire: props.hireId,
    first: 1,
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

const sourceHireLocalId = computed(() => {
  const globalId = result.value?.hire?.sourceHire?.id
  return globalId ? atob(globalId).split(':')[1] : null
})

const sourceHire = computed(() => result.value?.hire?.sourceHire ?? null)

// True when the client has changed the source hire and we haven't responded
// to that change on this mirror yet. The server decides; the frontend just
// reads the flag.
const hasUnactedClientChanges = computed(() => result.value?.hire?.hasUnactedClientChanges ?? false)

const hasUnactedClientTermination = computed(
  () => result.value?.hire?.hasUnactedClientTermination ?? false,
)

const { date: clientTerminationDate, isValid: hasClientTerminationDate } = useDate(
  computed(() => sourceHire.value?.terminationDate ?? ''),
)

const { date: supplierEndDate, isValid: hasSupplierEndDate } = useDate(
  computed(() => result.value?.hire?.activeContract?.endDate ?? ''),
)

const canTerminateContract = computed(() => result.value?.hire?.canTerminateContract ?? false)
const canCancelContract = computed(() => result.value?.hire?.canCancelContract ?? false)
const canManageContracts = computed(() => user.value.hasPermission('manage-contracts'))

const canTerminateSupplierHire = computed(
  () => canManageContracts.value && canTerminateContract.value,
)

const canCancelSupplierHire = computed(
  () => canManageContracts.value && !canTerminateContract.value && canCancelContract.value,
)

const endSupplierHireLabel = computed(() =>
  canTerminateSupplierHire.value ? $t('Terminate') : $t('Cancel hire'),
)

const canEndSupplierHire = computed(
  () => canTerminateSupplierHire.value || canCancelSupplierHire.value,
)

const openEndSupplierHire = () => {
  if (canTerminateSupplierHire.value) {
    openTerminateContractModal()
  } else {
    openCancelHireModal()
  }
}

// One row per field the client has changed. Empty when either contract is
// missing — the alert then shows the headline without a diff body.
const clientChangeDiff = useContractDiff(
  computed(() => sourceHire.value?.activeContract ?? null),
  computed(() => sourceHire.value?.draftContract ?? null),
)

const sourceHireBannerMessage = computed(() => {
  const status = sourceHire.value?.activeStatus
  const jobTitle = sourceHire.value?.job?.name
  const parts = [$t('Supplier version of a hire')]
  if (jobTitle) {
    parts[0] = `${$t('Supplier version of hire')}: ${jobTitle}`
  }
  if (status) {
    const formatted = status.charAt(0) + status.slice(1).toLowerCase()
    parts.push(`(${formatted})`)
  }
  return parts.join(' ')
})

const sourceHireDetails = computed(() => {
  const details: string[] = []
  if (sourceHire.value?.rate) {
    details.push(
      `${$t('Client rate')}: ${sourceHire.value.currency} ${sourceHire.value.rate} / ${sourceHire.value.rateType?.toLowerCase()}`,
    )
  }
  if (sourceHire.value?.startDate) {
    const period = sourceHire.value.endDate
      ? `${sourceHire.value.startDate} – ${sourceHire.value.endDate}`
      : sourceHire.value.startDate
    details.push(`${$t('Period')}: ${period}`)
  }
  return details
})

const supplierHireId = computed(() => {
  // The hire route binds by local key only, so the global id has to be decoded.
  const globalId = result.value?.hire?.supplierHire?.id
  return globalId ? atob(globalId).split(':')[1] : null
})

const hireActiveStatus = computed(() => result.value?.hire?.activeStatus ?? null)
const hireCompany = computed(() => result.value?.hire?.company ?? null)

const supplierBannerSubtitle = computed(() => {
  const name = hireCompany.value?.name
  return name ? `${$t('Client')}: ${name}` : undefined
})

const supplierBannerDetails = computed(() => {
  const status = hireActiveStatus.value
  if (!status || status === HireActiveStatus.Active) return []

  if (status === HireActiveStatus.Draft) {
    return [
      $t('This hire has not been offered by the client yet. Action is pending on the client side.'),
    ]
  }
  if (status === HireActiveStatus.Offered) {
    return [$t('The client has shared this hire. Awaiting engagement confirmation.')]
  }

  return []
})

const approvalApprovablesCount = computed(() => result.value?.approvals.paginatorInfo.count ?? 0)

const approvalsRequireActionCount = computed(
  () => result.value?.approvalsRequireAction.paginatorInfo.count ?? 0,
)

// We could probably improve this by querying the classification required compliance directly somehow
const classificationRequiresAction = computed(
  () =>
    (result.value?.hire?.classification?.isReclassificationRequired &&
      result.value?.hire?.usesClassification) ||
    result.value?.hire?.classification?.isClassificationActionRequired,
)

const shouldShowClassificationTab = computed(
  () => viewingAsClient.value && (result.value?.hire?.usesClassification ?? false),
)

const {
  hasChecklists: shouldShowChecklistsTab,
  checkCounts: checklistCounts,
  loading: checklistsLoading,
} = useHireChecklists(() => props.hireId)

const shouldShowPaymentsTab = computed(
  () =>
    viewingAsClient.value &&
    (props.paymentsTabEnabled ?? false) &&
    !loading.value &&
    result.value?.hire?.activeStatus !== HireActiveStatus.Draft,
)

const shouldShowContractsTab = computed(
  () =>
    !recruiterLoading.value &&
    canViewContractHistory({
      viewerIsSupplier: viewerIsSupplier.value,
      recruiterManagesWorkers: recruiterManagesWorkers.value,
      contractCount: result.value?.hire?.contracts?.paginatorInfo?.total ?? 0,
    }),
)

const pendingPaymentsVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  hires: [props.hireId],
  statuses: [PaymentRequestStatus.Unapproved],
  first: 1,
}))

const { result: pendingPaymentsResult } = useQuery(
  graphql(`
    query HirePagePendingPaymentRequestsIndicator(
      $accounts: [ID!]
      $hires: [ID!]
      $statuses: [PaymentRequestStatus!]
      $first: Int!
    ) {
      paymentRequests(accounts: $accounts, hires: $hires, statuses: $statuses, first: $first) {
        paginatorInfo {
          total
        }
      }
    }
  `),
  () => pendingPaymentsVariables.value,
  {
    clientId: 'publicApi',
    enabled: shouldShowPaymentsTab,
  },
)

const pendingPaymentsCount = computed(
  () => pendingPaymentsResult.value?.paymentRequests?.paginatorInfo?.total ?? 0,
)

const showPendingPaymentsIndicator = computed(
  () => shouldShowPaymentsTab.value && pendingPaymentsCount.value > 0,
)

const availableTabs = computed(() => {
  const tabs: HirePageTab[] = ['overview']
  if (shouldShowContractsTab.value) tabs.push('contracts')
  if (shouldShowPaymentsTab.value) tabs.push('payments')
  if (shouldShowChecklistsTab.value) tabs.push('checklists')
  if (shouldShowClassificationTab.value) tabs.push('classification')
  if (approvalApprovablesCount.value > 0 && viewingAsClient.value) tabs.push('approvals')

  return tabs
})

const hideTabBar = computed(
  () =>
    (viewerIsSupplier.value || isWorker.value) &&
    !viewingAsClient.value &&
    availableTabs.value.length <= 1,
)

const validateAndGetTab = computed(() => {
  const requestedTab = currentTab.value

  // A link straight to the Checklists tab waits for the checklists, rather than falling back to Overview first.
  if (loading.value || checklistsLoading.value) {
    return requestedTab
  }

  if (!isHirePageTab(requestedTab) || !availableTabs.value.includes(requestedTab)) {
    return 'overview'
  }

  return requestedTab
})

watch(validateAndGetTab, (newTab) => {
  const requestedTab = currentTab.value

  if (newTab !== requestedTab && requestedTab !== 'overview') {
    router.replace({
      path: `/hires/${props.hireId}/overview`,
      query: route.query,
    })
  }
})

const activeTab = ref(validateAndGetTab.value)

watch(validateAndGetTab, (newTab) => {
  activeTab.value = newTab
})

watch(activeTab, (newTab) => {
  router.push({
    path: `/hires/${props.hireId}/${newTab}`,
    query: route.query,
  })
})

const handleReloading = () => {
  refetch()

  publicApi.refetchQueries({
    include: queriesToRefetch,
  })
}
</script>

<style lang="scss" scoped>
.hire-page-tabs {
  &:deep(.wds-tab-content) {
    @apply tw-mt-0;
  }

  &:deep(.wds-tab-button--active .wds-tab-title) {
    @apply tw-text-lg tw-font-semibold;
  }

  &:deep(.wds-tab-title) {
    @apply tw-text-lg tw-font-medium;
  }

  &--no-bar :deep(.wds-tabs > .wds-stack) {
    display: none;
  }
}
</style>
